/***********************/
/* Angular + external */
import { Component, ElementRef, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBlotterActionEvent } from "../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxDialogData } from "./models/bfx-dialog-data.model";
// components
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
// services
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxOrderDialogsProcessorService } from "./services/bfx-order-dialogs-processor.service";

import { BfxSysConfigUtilService } from "shared/auth-services";
import {
  AlgoStrategyLookupService,
  ClientLookupService,
  IOrderNotifData,
  OrderSlippageService,
  StaticDataPrivatizationType,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import { OrderTypeHelper } from "shared/cubiq-services/model/orders/OrderTypeHelper";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

import { LoggerService } from "@bfx/logger";
import { isOrderNotifData } from "../../shared/cubiq-services/model/core/notifications/IOrderNotifData";
import { BfxConfigIDRefHelper } from "../../shared/helper-services/bfx-config-id-ref-helper";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-dialog-order-quick-fill-confirm",
  templateUrl: "./html/bfx-dialog-order-quick-fill-confirm.html",
  standalone: false,
})
export class BfxDialogOrderQuickFillConfirmComponent
  extends BfxDialogBaseComponent
  implements OnDestroy
{
  /***************************************/
  /* DATA */
  public config: any = {
    debug: false,
  };

  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 500,
      title_text: "Quick Fill",
      message_template: {
        single: "Are you sure you want to quick fill the selected order?",
        multi: "Are you sure you want to quick fill =Num= order(s):",
      },
      message_text: null,
      message_text_ids: null,
      message_text_ids_show_count: null,
      checkbox_template: {
        enabled: "STP Enabled",
        disabled: "STP Disabled",
      },
      checkbox_text: null,
      checkbox_show: null,
      checkbox_state: null,
      checkbox_disabled: null,
    },
    body: null,
  };

  private configurationSubscription: Subscription;
  private orderTypeHelper: OrderTypeHelper;
  private manualExecutionCallNameText: string = "";

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _configService: BfxConfigService,
    private _translationService: TranslateService,
    private _orderDialogProcessorService: BfxOrderDialogsProcessorService,
    private _element: ElementRef,
    private clientLookup: ClientLookupService,
    public sysConfigService: SystemConfigPromiseService,
    protected algoStrategyLookupService: AlgoStrategyLookupService,
    protected sysConfigUtil: BfxSysConfigUtilService,
    private orderSlippageService: OrderSlippageService,
  ) {
    super();
    this.subscribeToConfiguration();

    // in keeping with other dialogs - Don't set AON / partfill text -dc
    this.orderTypeHelper = new OrderTypeHelper(
      "",
      this.algoStrategyLookupService,
      this.sysConfigUtil,
    );
  }

  ngOnDestroy() {
    this.configurationSubscription.unsubscribe();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  /**********************************************************************/
  /* QUICK FILL CONFIRM - Show/Close Dialog */
  public openDialog(_dialogData: BfxDialogData): Observable<any> {
    this.data.dialogId = _dialogData.dialogId;
    this.data.body = _dialogData.body;
    this.data.payload = _dialogData.payload;
    let event: BfxBlotterActionEvent = _dialogData.event;
    if (
      event !== null &&
      event !== undefined &&
      event.selectedIds &&
      event.selectedIds.length > 0
    ) {
      // init
      this.data.actionEvent = event;
      let orderRefs: Array<string> = event.selectedItems.map((item) =>
        BfxConfigIDRefHelper.getOrderRef(item),
      );
      let message_text = "";
      let message_text_ids = "";
      let selectedItems: Array<CubiqTS.OrderDataImpl | IOrderNotifData> = event.selectedItems;

      if (orderRefs.length === 1) {
        let order: CubiqTS.OrderDataImpl = isOrderNotifData(selectedItems[0])
          ? selectedItems[0].fullOrderDataObj
          : selectedItems[0];
        let ccyPair: string = order.isInvertedOrderFg
          ? order.nonBaseCode + "/" + order.baseCode
          : order.baseCode + "/" + order.nonBaseCode;
        // privatization
        let client: CubiqTS.ClientImpl = StaticDataPrivatizationType.hasPrivatization(order)
          ? this.clientLookup.getItem(order.clientCode)
          : null;
        let clientIdText: string = client != null ? client.clientId : order.clientCode;

        let slippage: CubiqTS.BigNumber = this.orderSlippageService.getSlippage(order);
        let slippageMessage: string = slippage != null ? `with Slippage ${slippage.toFixed()}` : "";

        message_text += this.data.dialogData.message_template.single;

        if (order.orderType == CubiqTS.OrderType.LIMIT_CALL) {
          // <Client> <Instrument> @ <Request Rate> <Type> <Slippagge>
          message_text_ids = `${clientIdText} ${ccyPair} @ ${order.requestRt.toString()} ${this.orderTypeHelper.getType(
            order,
          )}`;
        } else {
          // <Client> <Instrument> <Intent> <Open Amt> <Ccy> @ <Request Rate> <Type> <Slippagge>
          message_text_ids =
            `${clientIdText} ` +
            `${CubiqTS.OrderIntent[order.clientIntent]}s
										${order.fixedCode}
										${order.leavesQty.toFormat(0)}
										${ccyPair}
										@ ${order.requestRt.toString()}
										${this.orderTypeHelper.getType(order)}
										${slippageMessage}`;
        }
      } else if (orderRefs.length > 1) {
        message_text += this.data.dialogData.message_template.multi;
        message_text = message_text.replace("=Num=", orderRefs.length.toString());
        message_text_ids = orderRefs.join(",\n");
      }

      // evaluate for STP checkbox and assign values
      this._orderDialogProcessorService.evaluateSTPCheckboxState(event).then((stpState) => {
        this.assignValuesAndShowConfirmDialog(
          message_text,
          message_text_ids,
          stpState.show,
          stpState.state,
          stpState.disabled,
        );
      });
    } else {
      logger.out.error(
        "ERROR: BfxDialogOrderQuickFillConfirmComponent: openDialog()= invalid event payload",
        event,
      );
    }
    return this.data.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    super.closeDialog();

    this.data.dialogData.message_text_ids = null;
    this.data.dialogData.message_text = null;
    this.data.actionEvent = null;
    this.data.payload = null;
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  /**********************************************************************/
  /* QUICK FILL CONFIRM - Actions */
  public actionQuickFillConfirm_confirm(): void {
    if (this.data.payload.callback) {
      this.data.payload.callback(this.data.actionEvent, this.data.dialogData.checkbox_state);
    }
    this.closeDialog();
  }

  public actionQuickFillConfirm_close(): void {
    if (this.data.payload.callbackNegative) {
      this.data.payload.callbackNegative(this.data.actionEvent);
    }
    this.closeDialog();
  }

  public onChangeQuickFillConfirm_checkbox(): void {
    if (this.data.dialogData.checkbox_state) {
      this.data.dialogData.checkbox_text = this.data.dialogData.checkbox_template.enabled;
    } else {
      this.data.dialogData.checkbox_text = this.data.dialogData.checkbox_template.disabled;
    }
  }

  /**********************************************************************/
  /* QUICK FILL CONFIRM - Helpers */
  private assignValuesAndShowConfirmDialog(
    message_text: string,
    message_text_ids: string,
    checkbox_show: boolean,
    checkbox_state: boolean,
    checkbox_disabled: boolean,
  ): void {
    // assign values
    this.data.dialogData.message_text = message_text;
    this.data.dialogData.message_text_ids = message_text_ids;
    // stp checkbox
    this.data.dialogData.checkbox_show = checkbox_show;
    this.data.dialogData.checkbox_state = checkbox_state;
    this.data.dialogData.checkbox_disabled = checkbox_disabled;
    this.data.dialogData.checkbox_text = checkbox_state
      ? this.data.dialogData.checkbox_template.enabled
      : this.data.dialogData.checkbox_template.disabled;
    // show the dialog
    NativeDesktopSupport.prepareEmbeddedDialog(this.data.body, this._dialog, this._element);

    this.data.dialogData.show = true;
  }
}
